import React from "react";
import {
    Box,
    Typography,
    Avatar,
    Grid,
    ListItem,
    List,
    Tooltip,
} from "@mui/material";
import RectangleIcon from "@mui/icons-material/Rectangle";
import moment from "moment";
import { useNavigate } from "react-router-dom";

// Define the type for the props this component receives
interface APIGatewayRequestsTableProps {
    gateways: any[];
}

const APIGatewayRequestsTable: React.FC<APIGatewayRequestsTableProps> = ({ gateways }) => {
    const [cardHoverIndex, setCardHoverIndex] = React.useState<number | null>(null);
    const navigate = useNavigate();

    // Helper function to determine the background color based on status
    const getStatusBgColor = (status: string) => {
        const lowerStatus = status?.toLowerCase();
        if (lowerStatus === "active") return "#FFFFFF";
        if (lowerStatus === "rejected") return "#F0443810"; // Light red
        return "#FDB02210"; // Light yellow
    };

    // Helper function to determine the status indicator color
    const getStatusColor = (status: string) => {
        const lowerStatus = status?.toLowerCase();
        if (lowerStatus === "active") return "#12B76A"; // Green
        if (lowerStatus === "rejected") return "#F04438"; // Red
        return "#FDB022"; // Yellow
    };

    return (
        <List sx={{ bgcolor: "#fafafa" }}>
            <ListItem sx={{ bgcolor: "#f2f2f2", borderRadius: "4px" }}>
                <Grid container direction="row" alignItems="center">
                    {/* Adjusted xs values for proper spacing */}
                    <Grid item xs={3}><Typography fontSize="12px" fontFamily="Gilroy-SemiBold">GATEWAY NAME</Typography></Grid>
                    <Grid item xs={1.25}><Typography fontSize="12px" fontFamily="Gilroy-SemiBold">API TYPE</Typography></Grid>
                    <Grid item xs={1.5}><Typography fontSize="12px" fontFamily="Gilroy-SemiBold">ENDPOINT TYPE</Typography></Grid>
                    <Grid item xs={1.25}><Typography fontSize="12px" fontFamily="Gilroy-SemiBold">REGION</Typography></Grid>
                    <Grid item xs={1.5}><Typography fontSize="12px" fontFamily="Gilroy-SemiBold">OWNER</Typography></Grid>
                    <Grid item xs={2}><Typography fontSize="12px" fontFamily="Gilroy-SemiBold">ACTIONED BY / REASON</Typography></Grid>
                    <Grid item xs={1.5}><Typography fontSize="12px" fontFamily="Gilroy-SemiBold">ACTIONED ON</Typography></Grid>
                </Grid>
            </ListItem>
            {gateways.map((gateway: any, index: number) => (
                <ListItem
                    key={gateway.id}
                    sx={{
                        border: cardHoverIndex === index ? "1px solid #000" : "1px solid #00000000",
                        mt: 1,
                        cursor: "pointer",
                        bgcolor: getStatusBgColor(gateway.status),
                        borderRadius: "8px",
                    }}
                    onClick={() => navigate(`/apis-gateway/requests/details/${gateway.id}`)}
                    onMouseEnter={() => setCardHoverIndex(index)}
                    onMouseLeave={() => setCardHoverIndex(null)}
                >
                    <Grid container direction="row" alignItems="center">
                        {/* Adjusted xs values for data cells */}
                        <Grid item xs={3}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                 <RectangleIcon sx={{ 
                                     color: getStatusColor(gateway.status), 
                                     bgcolor: getStatusColor(gateway.status), 
                                     width: "4px", 
                                     height: "24px", 
                                     borderRadius: "2px", 
                                     marginRight: "8px" 
                                 }} />
                                <Box>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Medium">{gateway.name}</Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={1.25}><Typography fontSize="14px" fontFamily="Gilroy-Regular">{gateway.apiType || 'N/A'}</Typography></Grid>
                        <Grid item xs={1.5}><Typography fontSize="14px" fontFamily="Gilroy-Regular">{gateway.endpointType || 'N/A'}</Typography></Grid>
                        <Grid item xs={1.25}><Typography fontSize="14px" fontFamily="Gilroy-Regular">{gateway.region || 'N/A'}</Typography></Grid>
                        <Grid item xs={1.5}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Avatar 
                                    src={gateway.googleProfilePicture}
                                    sx={{ mr: 1, height: 30, width: 30 }}
                                > 
                                    {gateway.solutionOwner?.charAt(0).toUpperCase()}
                                </Avatar>
                                <Typography fontSize="14px" fontFamily="Gilroy-Regular">{gateway.solutionOwner}</Typography>
                            </Box>
                        </Grid>
                        
                        {/* --- ACTIONED BY COLUMN --- */}
                        <Grid item xs={2}>
                            {gateway.status !== "pending" && (
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    {gateway.apiApproverName && (
                                        <Avatar 
                                            src={gateway.apiApproverAvatar}
                                            sx={{ mr: 1, height: 30, width: 30 }}
                                        >
                                            {gateway.apiApproverName.charAt(0).toUpperCase()}
                                        </Avatar>
                                    )}
                                    {gateway.apiApproverName && <Typography fontSize="14px" fontFamily="Gilroy-Regular" mr={1}>{gateway.apiApproverName}</Typography>}
                                    {gateway.status === "rejected" && gateway.reasonForRejection && (
                                        <Tooltip title={`Reason: ${gateway.reasonForRejection}`}><img src="/assets/info.svg" alt="info" /></Tooltip>
                                    )}
                                </Box>
                            )}
                        </Grid>
                        
                        {/* --- ACTIONED ON COLUMN --- */}
                        <Grid item xs={1.5}>
                            {gateway.status !== "pending" && gateway.actionedAt && (
                                <Typography fontSize="14px" fontFamily="Gilroy-Regular">{moment(gateway.actionedAt).format("DD/MM/YYYY")}</Typography>
                            )}
                        </Grid>
                    </Grid>
                </ListItem>
            ))}
        </List>
    );
};

export default APIGatewayRequestsTable;